<template>
  <div class="tw-flex tw-flex-col tw-gap-3">
    <div
      class="
        tw-flex
        tw-items-center
        tw-gap-3
        tw-min-h-[52px]
        tw-rounded-[10px]
        tw-border
        tw-border-solid
        tw-border-sf-neutral-lighter-2
        tw-bg-white
        tw-p-3
        tw-shadow-md
      "
    >
      <textarea
        ref="textareaRef"
        :value="draft"
        :placeholder="
          loading
            ? I18n.t('companies_overview.assistant.input_placeholder_loading')
            : I18n.t('companies_overview.assistant.input_placeholder')
        "
        rows="1"
        class="
          assistant-input__field
          tw-min-w-0 tw-flex-1 tw-resize-none tw-appearance-none
          !tw-m-0 !tw-p-0 !tw-border-0 !tw-bg-transparent !tw-shadow-none
          focus:!tw-shadow-none
          !tw-outline-none
          focus:!tw-outline-none
          tw-text-sm tw-text-sf-neutral-darker-1
          placeholder:tw-text-sf-neutral-light-2
          placeholder:!tw-not-italic
        "
        data-testid="assistant-input"
        @input="onInput"
        @keydown="onKeydown"
      ></textarea>

      <!-- Stop while a reply is loading, otherwise Submit -->
      <SfButtonIcon
        v-if="loading"
        icon="stop"
        small
        :aria-label="I18n.t('companies_overview.assistant.stop')"
        data-testid="assistant-stop"
        @click="cancel"
      />
      <SfButtonIcon
        v-else
        icon="arrow-up"
        variant="ai"
        small
        :disabled="!canSubmit"
        :aria-label="I18n.t('companies_overview.assistant.submit')"
        data-testid="assistant-submit"
        @click="submit"
      />
    </div>

    <p
      class="
        tw-m-0 tw-px-6 tw-text-center tw-text-xs tw-text-sf-neutral-light-2
      "
    >
      {{ I18n.t('companies_overview.assistant.disclaimer') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import I18n from '@app/adapters/I18n'
import { useAssistantStore } from '@app/insights/store/assistant'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'

// Cap the auto-growing textarea before it scrolls internally.
const MAX_INPUT_HEIGHT = 160

const assistantStore = useAssistantStore()
const { draft, loading } = storeToRefs(assistantStore)
const { setDraft, submitPrompt, cancel } = assistantStore

const textareaRef = ref<HTMLTextAreaElement | null>(null)

const canSubmit = computed(
  () => draft.value.trim().length > 0 && !loading.value
)

function resize(): void {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`
  el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden'
}

function onInput(event: Event): void {
  setDraft((event.target as HTMLTextAreaElement).value)
  void nextTick(resize)
}

function submit(): void {
  if (!canSubmit.value) return
  void submitPrompt()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    submit()
  }
}

function focus(): void {
  textareaRef.value?.focus()
}

watch(draft, (value, previous) => {
  void nextTick(resize)
  // Focus when text appears from outside the input (an empty-state paste).
  if (value && !previous) focus()
})

onMounted(() => {
  focus()
  resize()
})
</script>
